import { BusinessService } from './shared/services/business.service';
import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { ActivatedRoute, RouterOutlet } from '@angular/router';
import { AppService } from './app.service';
import { ModalService } from './shared/utils/services/modal.service';
import { SplashLoaderComponent } from './shared/ui/splash-loader/splash-loader.component';
import { TranslocoModule } from '@jsverse/transloco';
import { BusinessCategory } from './shared/schemas/business_category';
import { Locale } from './shared/schemas/locale';
import { City } from './shared/schemas/cityLocale';
import { DirectusFileSchema } from './shared/schemas/business';
import { environment } from '../environments/environment';
import { BusinessContact } from './shared/schemas/business_contact';
import { NewPaymentComponent } from './shared/features/new-payment/new-payment.component';
import { BossServicesService } from './shared/services/boss-services.service';
import { PaymentAccount } from './shared/schemas/payment_account';

@Component({
  selector: 'app-root',
  imports: [
    CommonModule,
    SplashLoaderComponent,
    TranslocoModule,
    NewPaymentComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  public appService = inject(AppService);
  public modalService = inject(ModalService);
  private businessService = inject(BusinessService);
  private services = inject(BossServicesService);
  env = environment;
  business = this.businessService.business;
  businessPaymentAccounts = this.businessService.businessPaymentAccounts;
  transaction = this.businessService.transaction;
  businessAddress = this.businessService.address;
  businessEffect = effect(() => {
    if (this.business()) {
      this.name.set(this.business()?.name!);
      this.store_name.set(this.business()?.store_name!);
      this.logo.set(this.business()?.logo! as DirectusFileSchema);
      this.banner.set(this.business()?.banner! as DirectusFileSchema);
      this.wifiName.set(this.business()?.wifi_name!);
      this.wifiPassword.set(this.business()?.wifi_password!);
      this.selectedCategory.set(this.business()?.category!);
      this.selectedCountry.set((this.business()?.country as Locale) || null);
      this.businessService.contacts().forEach((contact: BusinessContact) => {
        if (contact?.is_phone_number) {
          const contactNumber = contact?.phone_number || '';
          if (contactNumber && this.selectedCountry()) {
            const code = this.selectedCountry()?.phone_code || '';
            const formattedNumber = contactNumber.replace(code, '');
            this.phoneNumber.set(formattedNumber);
          }
        } else {
          if (contact.email) {
            this.email.set(contact.email);
          }
        }
      });
    }

    if (this.businessAddress()) {
      this.selectedCity.set((this.businessAddress()?.city! as City) || null);
      this.citiesLoaded.set(true);
      this.description.set(this.businessAddress()?.street_address!);
    }

    if (this.transaction()) {
      this.amount.set(this.transaction()?.amount!);
      const customerNumber = this.transaction()?.customer_number || null;
      const code = this.selectedCountry()?.phone_code || '';
      const formattedNumber = customerNumber?.replace(code, '') || '';
      if (formattedNumber) {
        this.customerNumber.set(formattedNumber);
      }
    }
  });

  logo = signal<DirectusFileSchema | null>(null);
  logoLink = computed<string>(() => {
    return this.logo() !== null
      ? `${this.env.apiUrl}/assets/${this.logo()!.id}/${this.logo()!.filename_disk}`
      : '';
  });
  banner = signal<DirectusFileSchema | null>(null);
  bannerLink = computed<string>(() => {
    return this.banner() !== null
      ? `${this.env.apiUrl}/assets/${this.banner()!.id}/${this.banner()!.filename_disk}`
      : '';
  });

  name = signal<string>('');
  store_name = signal<string>('');
  categories = signal<BusinessCategory[]>([]);
  countries = signal<Locale[]>([]);
  cities = signal<City[]>([]);
  referrers = signal<any[]>([]);
  wifiName = signal<string>('');
  wifiPassword = signal<string>('');
  wifiString = `WIFI:T:WPA;S:${this.wifiName()};P:${this.wifiPassword()};H:false;;`;

  selectedCountry = signal<Locale | null>(null);
  selectedCity = signal<City | null>(null);
  selectedCategory = signal<BusinessCategory | null>(null);
  selectedAccount = signal<PaymentAccount | null>(null);

  phoneNumberID = signal<string>('');
  phoneNumber = signal<string | null>(null);
  phoneNumberFormatted = computed<string>(() => {
    const code = this.selectedCountry()?.phone_code;
    if (!this.phoneNumber()) {
      return '';
    }
    return `${code}${this.phoneNumber()}`;
  });

  customerNumber = signal<string | null>(null);

  phoneNumberDisplay = computed<string>(() => {
    const code = this.selectedCountry()?.phone_code;
    const starts_with = this.selectedCountry()?.starts_with;

    if (this.phoneNumberFormatted().startsWith(code!) && starts_with) {
      const index = this.phoneNumberFormatted().indexOf(starts_with!);

      if (index !== -1) {
        const sliceIndex = index + starts_with!.length;
        const remainder = this.phoneNumberFormatted().slice(sliceIndex);

        return `${starts_with}${remainder}`;
      }
    }

    return this.phoneNumberFormatted();
  });

  callString = computed<string>(() => {
    return `tel:${this.phoneNumberFormatted()}`;
  });

  emailID = signal<string>('');
  email = signal<string>('');

  description = signal<string>('');
  citiesLoaded = signal<boolean>(false);
  accountID = signal<string | null>(null);
  transactionID = signal<string | null>(null);
  amount = signal<string | null>(null);
  loading = signal<boolean>(true);
  isTransaction = signal<boolean>(false);

  constructor(private activatedRoute: ActivatedRoute) {}

  async ngOnInit(): Promise<void> {
    await this.appService.init();

    const account = this.activatedRoute.snapshot.queryParamMap.get('a');
    const businessCode = this.activatedRoute.snapshot.queryParamMap.get('b');
    const amount = this.activatedRoute.snapshot.queryParamMap.get('am');
    const transaction = this.activatedRoute.snapshot.queryParamMap.get('t');

    this.accountID.set(account);
    this.transactionID.set(transaction);
    this.amount.set(amount);

    if (transaction) {
      this.isTransaction.set(true);
      await this.businessService.getBusinessTransaction(transaction);
      this.selectAccount(this.businessPaymentAccounts()[0]);
    } else if (account) {
      await this.businessService.getBusinessPaymentAccount(account);
    } else if (businessCode) {
      await this.businessService.getBusiness(businessCode);
    }
    this.loading.set(false);
  }

  selectAccount(account: PaymentAccount) {
    this.selectedAccount.set(account);
    this.modalService.closeAll();
    this.services.isNewPayment.set(true);
  }

  // newPayment() {
  //   this.modalService.closeAll();
  //   this.services.isNewPayment.set(true);
  // }
}
